import { useState, useCallback, useEffect } from 'react';
import { companyAPI } from '../../../services/api';

export interface CostItem {
  recipe_id: number;
  quantity: number;
  margin_pct: number;
}

export interface LineCost {
  name: string;
  quantity: number;
  unit: string;
  cost_per_unit: number | null;
  ingredient_unit: string;
  line_total: number | null;
  has_cost: boolean;
}

export interface RecipeCostResult {
  recipe_id: number;
  recipe_name: string;
  recipe_type: string;
  quantity_batches: number;
  pieces_per_batch: number;
  yield_unit: string;
  total_pieces: number;
  margin_pct: number;
  overhead_pct: number;
  ingredient_cost_per_batch: number;
  overhead_per_batch: number;
  cost_per_batch: number;
  cost_per_piece: number;
  selling_price_per_piece: number;
  profit_per_piece: number;
  total_cost: number;
  total_revenue: number;
  total_profit: number;
  line_costs: LineCost[];
  missing_cost_ingredients: string[];
  costing_complete: boolean;
}

export interface CostResult {
  results: RecipeCostResult[];
  grand_total_cost: number;
  overhead_pct: number;
}

export function useCostCalculator() {
  const [recipes, setRecipes] = useState<any[]>([]);
  const [loadingRecipes, setLoadingRecipes] = useState(true);

  const [items, setItems] = useState<(CostItem & { _key: number })[]>([]);
  const [overheadPct, setOverheadPct] = useState(15);

  const [result, setResult] = useState<CostResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRecipe, setExpandedRecipe] = useState<number | null>(null);

  const fetchRecipes = useCallback(async () => {
    try {
      const res = await companyAPI.getRecipes();
      setRecipes(Array.isArray(res.data) ? res.data : []);
    } catch {
      // ignore
    } finally {
      setLoadingRecipes(false);
    }
  }, []);

  useEffect(() => { fetchRecipes(); }, [fetchRecipes]);

  const addItem = () => {
    setItems(prev => [...prev, { _key: Date.now(), recipe_id: 0, quantity: 1, margin_pct: 30 }]);
  };

  const removeItem = (key: number) => {
    setItems(prev => prev.filter(i => i._key !== key));
  };

  const updateItem = (key: number, field: keyof CostItem, value: any) => {
    setItems(prev => prev.map(i => i._key === key ? { ...i, [field]: value } : i));
  };

  const calculate = async () => {
    const valid = items.filter(i => i.recipe_id > 0);
    if (!valid.length) {
      setError('Add at least one recipe.');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await companyAPI.recipeCost({
        items: valid.map(({ recipe_id, quantity, margin_pct }) => ({ recipe_id, quantity, margin_pct })),
        overhead_pct: overheadPct,
      });
      setResult(res.data);
      setExpandedRecipe(res.data.results[0]?.recipe_id ?? null);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to calculate costs.');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n: number, decimals = 2) =>
    n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  return {
    recipes, loadingRecipes,
    items, overheadPct, setOverheadPct,
    addItem, removeItem, updateItem,
    result, loading, error,
    expandedRecipe, setExpandedRecipe,
    calculate, fmt,
  };
}
